import { useState } from "react";
import { motion } from "framer-motion";
import { Search, AlertCircle, CheckCircle, Loader2, Download, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecoverMeetings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [orphanedAudios, setOrphanedAudios] = useState([]);
  const [recovering, setRecovering] = useState({});
  const [unsticking, setUnsticking] = useState(false);
  const [unstuckResult, setUnstuckResult] = useState(null);

  const unstickMeetings = async () => {
    setUnsticking(true);
    setUnstuckResult(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/recovery/unstick-meetings`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUnstuckResult(response.data);
      if (response.data.unstuck_count > 0) {
        toast.success(`${response.data.unstuck_count} reunión(es) liberada(s). Ve al Dashboard y usa "Reprocesar Audio".`);
      } else {
        toast.success("No hay reuniones atascadas");
      }
    } catch (error) {
      console.error("Error unsticking meetings:", error);
      toast.error(error.response?.data?.detail || "Error al liberar reuniones atascadas");
    } finally {
      setUnsticking(false);
    }
  };

  const searchOrphanedAudios = async () => {
    setSearching(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/recovery/orphaned-audios`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setOrphanedAudios(response.data.orphaned_audios || []);
      
      if (response.data.orphaned_audios.length === 0) {
        toast.success("No se encontraron grabaciones perdidas");
      } else {
        toast.success(`Se encontraron ${response.data.orphaned_audios.length} grabaciones perdidas`);
      }
    } catch (error) {
      console.error("Error searching orphaned audios:", error);
      toast.error("Error al buscar grabaciones perdidas");
    } finally {
      setSearching(false);
    }
  };

  const recoverAudio = async (meetingId) => {
    setRecovering(prev => ({ ...prev, [meetingId]: true }));
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/recovery/recover-audio/${meetingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Reunión recuperada y procesando...");
        // Remove from list
        setOrphanedAudios(prev => prev.filter(a => a.meeting_id !== meetingId));
      }
    } catch (error) {
      console.error("Error recovering audio:", error);
      toast.error(error.response?.data?.detail || "Error al recuperar grabación");
    } finally {
      setRecovering(prev => ({ ...prev, [meetingId]: false }));
    }
  };

  const recoverAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/recovery/recover-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Recuperadas ${response.data.recovered_count} reuniones`);
      setOrphanedAudios([]);
    } catch (error) {
      console.error("Error recovering all:", error);
      toast.error("Error al recuperar todas las grabaciones");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-5xl mx-auto p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-stone-900 mb-2">
            Recuperar Grabaciones Perdidas
          </h1>
          <p className="text-stone-600">
            Busca grabaciones de audio que no tienen reunión asociada
          </p>
        </motion.div>

        {/* Unstick Stuck Meetings */}
        <Card className="mb-6 border-orange-200 bg-orange-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Unlock className="w-5 h-5 text-orange-600" />
              Liberar Reuniones Atascadas
            </CardTitle>
            <CardDescription>
              ¿Tu reunión lleva mucho tiempo en "Pendiente" o "Procesando"? Libérala aquí para poder reprocesarla.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={unstickMeetings}
              disabled={unsticking}
              variant="outline"
              className="border-orange-300 hover:bg-orange-100"
              data-testid="unstick-meetings-btn"
            >
              {unsticking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Liberando...
                </>
              ) : (
                <>
                  <Unlock className="w-4 h-4 mr-2" />
                  Liberar Reuniones Atascadas
                </>
              )}
            </Button>
            {unstuckResult && unstuckResult.meetings && unstuckResult.meetings.length > 0 && (
              <div className="mt-4 text-sm text-stone-700" data-testid="unstuck-result">
                <p className="font-medium mb-2">Reuniones liberadas:</p>
                <ul className="list-disc list-inside space-y-1">
                  {unstuckResult.meetings.map((m) => (
                    <li key={m.id}>{m.title || m.id}</li>
                  ))}
                </ul>
                <p className="mt-3 text-stone-600">
                  Ahora ve al Dashboard, abre la reunión y pulsa <strong>"Reprocesar Audio"</strong>.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Search Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Buscar Grabaciones
            </CardTitle>
            <CardDescription>
              Escanea el servidor en busca de archivos de audio sin reunión asociada
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={searchOrphanedAudios}
              disabled={searching}
              className="w-full sm:w-auto"
            >
              {searching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Buscando...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Buscar Grabaciones Perdidas
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        {orphanedAudios.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-stone-900">
                Grabaciones Encontradas ({orphanedAudios.length})
              </h2>
              <Button
                onClick={recoverAll}
                disabled={loading}
                variant="default"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Recuperando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Recuperar Todas
                  </>
                )}
              </Button>
            </div>

            <div className="grid gap-4">
              {orphanedAudios.map((audio, index) => (
                <motion.div
                  key={audio.meeting_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertCircle className="w-5 h-5 text-orange-500" />
                            <h3 className="font-semibold text-stone-900">
                              {audio.filename}
                            </h3>
                          </div>
                          
                          <div className="space-y-1 text-sm text-stone-600">
                            <p>
                              <span className="font-medium">Tamaño:</span> {audio.size_mb} MB
                            </p>
                            <p>
                              <span className="font-medium">Fecha:</span>{" "}
                              {new Date(audio.created_at).toLocaleString('es-ES', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            <p className="text-xs font-mono text-stone-500">
                              ID: {audio.meeting_id}
                            </p>
                          </div>
                        </div>

                        <Button
                          onClick={() => recoverAudio(audio.meeting_id)}
                          disabled={recovering[audio.meeting_id]}
                          size="sm"
                        >
                          {recovering[audio.meeting_id] ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Recuperando...
                            </>
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-2" />
                              Recuperar
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {orphanedAudios.length === 0 && !searching && (
          <Card className="text-center py-12">
            <CardContent>
              <div className="text-stone-400 mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-stone-700 mb-2">
                No hay resultados
              </h3>
              <p className="text-stone-600">
                Haz clic en "Buscar Grabaciones Perdidas" para escanear el servidor
              </p>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              ¿Qué son las grabaciones perdidas?
            </h3>
            <p className="text-blue-800 text-sm mb-4">
              Son archivos de audio que se guardaron en el servidor pero que no tienen una reunión asociada en la base de datos. 
              Esto puede ocurrir si:
            </p>
            <ul className="text-blue-800 text-sm space-y-1 list-disc list-inside">
              <li>El navegador se cerró antes de completar el guardado</li>
              <li>Hubo un error de conexión durante la creación de la reunión</li>
              <li>La base de datos no estaba disponible momentáneamente</li>
            </ul>
            <p className="text-blue-800 text-sm mt-4">
              Al recuperarlas, se crearán las reuniones en la base de datos y se procesará el audio automáticamente.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RecoverMeetings;
